import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EChartsOption } from 'echarts';
import type { ECharts } from 'echarts';
import { RecordData, AttemptDetail } from 'src/app/types';
import { GOST_I18N } from '../../../cards/recordings/gost.i18n';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import {
  MeasurementEvent,
  MwstKpi,
  ParsedMeasurement,
} from '../../models/parsed-measurement.model';
import { NeckOrientation } from '../../services/neck-angle.service';

import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import {
  ReferenceCardComponent,
  SearchLink,
} from '../shared/reference-card/reference-card.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';

export interface MwstAttemptRow {
  trial: number;
  /** 嚥下の有無 (あり/なし) */
  swallowPresent: boolean;
  swallowLabel: string;
  /** せき・むせの有無 */
  coughPresent: boolean;
  coughLabel: string;
  /** 呼吸変化の有無 */
  respiratoryWarning: boolean;
  respiratoryLabel: string;
  /** 評点 (1-5) */
  interimScore: number;
  hasInterimScore: boolean;
}

interface MwstResultItem {
  value: number;
  description: string;
  selected: boolean;
}

@Component({
  selector: 'app-protocol-mwst',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    ReferenceCardComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-mwst.component.html',
  styleUrl: './protocol-mwst.component.scss',
})
export class ProtocolMwstComponent implements OnDestroy {
  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(0);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) return;
      this.waveHostReady.emit(comp);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
  }

  // ── Local state ──
  protected readonly attemptsExpanded = signal(true);

  protected toggleAttempts(): void {
    this.attemptsExpanded.update(v => !v);
  }

  // ── Computed ──
  protected readonly isFoodTest = computed(() => {
    const rt = this.recordData()?.data?.recordType as string | undefined;
    return rt === 'FOOD_TEST' || rt === 'FOOD_TEST_G' || rt === 'FOOD_TEST_G_DATA';
  });

  protected readonly headerTitle = computed(() =>
    this.isFoodTest()
      ? $localize`:@@details.protocolMwst.headerTitleFoodTest:フードテスト`
      : $localize`:@@details.protocolMwst.headerTitleMwst:MWST｜改訂水飲みテスト`,
  );

  protected readonly patientName = computed(() =>
    this.recordData()?.data?.userName ?? '',
  );

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });

  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) return $localize`:@@details.protocolMwst.measurer:計測者: ${d.recorderName}`;
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) return $localize`:@@details.protocolMwst.measurer:計測者: ${(m.lastName ?? '') + (m.firstName ?? '')}`;
    return '';
  });

  protected readonly mwstKpi = computed<MwstKpi | undefined>(() =>
    this.parsedMeasurement()?.mwstKpi,
  );

  protected readonly finalScore = computed(() => {
    const kpi = this.mwstKpi();
    if (kpi) return kpi.finalScore;
    const d = this.recordData()?.data as { finalScore?: number } | undefined;
    return d?.finalScore ?? 0;
  });

  /** 評点未入力（1〜5の有効値でない）かどうか */
  protected readonly hasFinalScore = computed(() => {
    const score = this.finalScore();
    return score >= 1 && score <= 5;
  });

  protected readonly attemptCount = computed(() => {
    const kpi = this.mwstKpi();
    if (kpi) return kpi.attemptCount;
    return this.resolveAttempts().length;
  });

  protected readonly attemptsDetail = computed<MwstAttemptRow[]>(() =>
    this.resolveAttempts().map((attempt, i) => this.toAttemptRow(attempt, i)),
  );

  /** 全5件の評点基準（PC用）— FoodTest / MWST で基準テキストが異なる */
  protected readonly mwstResults = computed<MwstResultItem[]>(() => {
    const score = this.finalScore();
    const source = this.isFoodTest() ? GOST_I18N.foodTest.results : GOST_I18N.mwst.results;
    return source.map(r => ({
      value: r.value,
      description: r.description,
      selected: r.value === score,
    }));
  });

  /** 該当スコア ± 1 の最大3件（モバイル用） */
  protected readonly mwstResultsNearby = computed<MwstResultItem[]>(() => {
    const all = this.mwstResults();
    const score = this.finalScore();
    if (score <= 0) return all.slice(0, 3);
    return all.filter(r => r.value >= score - 1 && r.value <= score + 1);
  });

  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly dataId = computed(() => this.recordData()?.id ?? '');

  protected readonly downloadUrl = computed(() =>
    this.recordData()?.data?.storage?.csvUrl ?? '',
  );

  protected readonly searchLinks = computed<SearchLink[]>(() => {
    const ft = this.isFoodTest();
    const links: SearchLink[] = ft
      ? [
          { label: $localize`:@@details.protocolMwst.searchLinkFoodTestScreening:フードテスト 嚥下スクリーニング`, url: 'https://www.google.com/search?q=フードテスト+嚥下+スクリーニング' },
          { label: $localize`:@@details.protocolMwst.searchLinkFoodTestCriteria:フードテスト 評価基準`, url: 'https://www.google.com/search?q=フードテスト+嚥下+評価基準' },
        ]
      : [
          { label: $localize`:@@details.protocolMwst.searchLinkMwst:MWST（改訂水飲みテスト）`, url: 'https://www.google.com/search?q=MWST+改訂水飲みテスト' },
          { label: $localize`:@@details.protocolMwst.searchLinkMwstCriteria:MWST 評価基準`, url: 'https://www.google.com/search?q=MWST+評価基準' },
        ];
    if (this.finalScore() > 0 && this.finalScore() <= 3) {
      links.push(
        { label: $localize`:@@details.protocolMwst.searchLinkAspirationPrevention:誤嚥予防 リハビリテーション`, url: 'https://www.google.com/search?q=誤嚥予防+リハビリテーション' },
      );
    }
    return links;
  });

  protected readonly disclaimer = computed(() =>
    this.isFoodTest()
      ? $localize`:@@details.protocolMwst.disclaimerFoodTest:フードテストはスクリーニング検査です。判定には観察所見とAI推定値を含むため、最終的な臨床判断は専門医の評価と併せて行ってください。`
      : $localize`:@@details.protocolMwst.disclaimerMwst:MWSTはスクリーニング検査です。判定には観察所見とAI推定値を含むため、最終的な臨床判断は専門医の評価と併せて行ってください。`,
  );

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }

  // ── Private ──
  private resolveAttempts(): AttemptDetail[] {
    const kpi = this.mwstKpi();
    if (kpi && Array.isArray(kpi.attempts) && kpi.attempts.length > 0) {
      return kpi.attempts as AttemptDetail[];
    }
    const d = this.recordData()?.data as { attemptsDetail?: AttemptDetail[] } | undefined;
    return Array.isArray(d?.attemptsDetail) ? d!.attemptsDetail : [];
  }

  private toAttemptRow(attempt: AttemptDetail, index: number): MwstAttemptRow {
    const wp = attempt.waterPuddingSwallowPart;
    const interimScore = attempt.interimScore ?? 0;
    const hasInterimScore = attempt.interimScore != null;

    const swallow = this.resolvePresence(wp?.swallowResult);
    const cough = this.resolveCoughPresence(wp?.coughCount, wp?.coughOrOtherResult);
    const respiratory = this.resolvePresence(wp?.dyspneaResult);

    return {
      trial: index + 1,
      swallowPresent: swallow.present,
      swallowLabel: swallow.label,
      coughPresent: cough.present,
      coughLabel: cough.label,
      respiratoryWarning: respiratory.present,
      respiratoryLabel: respiratory.label,
      interimScore,
      hasInterimScore,
    };
  }

  /**
   * TP/TN/FP/FN/true/false/あり/なし 等を「あり」「なし」に正規化。
   *
   * 混同行列に基づくマッピング（gost-utils.ts convertToTPTN と同じ論理）:
   * - TP (自動○ 実際○), FN (自動× 実際○) → 実際に事象あり → 「あり」
   * - TN (自動× 実際×), FP (自動○ 実際×) → 実際に事象なし → 「なし」
   */
  private resolvePresence(value: unknown): { label: string; present: boolean } {
    const str = String(value ?? '').trim().toLowerCase();
    if (!str) return { label: '—', present: false };
    // TP, FN = 実際に事象あり → あり
    if (['true', 'tp', 'fn', '1'].includes(str) || str.includes('あり') || str.includes('切迫')) {
      return { label: $localize`:@@details.protocolMwst.present:あり`, present: true };
    }
    // TN, FP = 実際に事象なし → なし
    if (['false', 'tn', 'fp', '0'].includes(str) || str.includes('なし') || str.includes('良好')) {
      return { label: $localize`:@@details.protocolMwst.absent:なし`, present: false };
    }
    return { label: String(value).trim() || '—', present: false };
  }

  /** 咳嗽は coughCount > 0 または coughOrOtherResult から判定 */
  private resolveCoughPresence(
    coughCount: number | undefined,
    coughOrOtherResult: string | undefined,
  ): { label: string; present: boolean } {
    // coughOrOtherResult が明示的にある場合はそちらを優先
    if (coughOrOtherResult != null && String(coughOrOtherResult).trim()) {
      return this.resolvePresence(coughOrOtherResult);
    }
    // coughCount で判定
    if (coughCount != null) {
      return coughCount > 0
        ? { label: $localize`:@@details.protocolMwst.present:あり`, present: true }
        : { label: $localize`:@@details.protocolMwst.absent:なし`, present: false };
    }
    return { label: '—', present: false };
  }

}
